/**
 * Export Templates for Agent Outputs
 * Generates professional, client-ready documents in PDF, DOCX, and Markdown formats.
 * Driven by agent output structure, not hardcoded per agent.
 */

import { getOutputStyleProfile } from './outputStyles';

// Brand configuration
const BRAND = {
  name: '',
  tagline: '',
  primaryColor: '#00E5C0',
  secondaryColor: '#00B4D8'
};

/**
 * Generate report header
 */
const generateHeader = (session, organization, styleProfile) => {
  const date = new Date(session.completed_at || session.created_at);
  
  return {
    title: session.agent_name || 'Analysis Report',
    subtitle: styleProfile.name + ' Report',
    organization: organization?.name || 'Organization',
    date: date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    generatedBy: organization?.name || '',
    accentColor: styleProfile.colorAccent
  };
};

/**
 * Extract and structure content from session output
 */
const extractStructuredContent = (session, styleProfile) => {
  const output = session.output || {};
  const content = output.content || {};
  const sections = content.sections || [];
  const markdown = content.markdown || '';
  
  const result = {
    executiveSummary: null,
    keyFindings: [],
    analysis: [],
    recommendations: [],
    appendices: [],
    rawMarkdown: markdown
  };
  
  // Process sections based on style profile's section order
  const sectionOrder = styleProfile.formatting.sectionOrder;
  
  sections.forEach(section => {
    const key = (section.key || section.label || '').toLowerCase();
    const sectionContent = {
      title: section.label || section.key,
      content: section.content,
      type: section.type || 'text'
    };
    
    if (key.includes('summary') || key.includes('overview') || key.includes('executive')) {
      result.executiveSummary = sectionContent;
    } else if (key.includes('finding') || key.includes('insight') || key.includes('key')) {
      result.keyFindings.push(sectionContent);
    } else if (key.includes('analysis') || key.includes('breakdown') || key.includes('detail')) {
      result.analysis.push(sectionContent);
    } else if (key.includes('recommendation') || key.includes('action') || key.includes('next')) {
      result.recommendations.push(sectionContent);
    } else if (key.includes('appendix') || key.includes('data') || key.includes('table')) {
      result.appendices.push(sectionContent);
    } else {
      // Add to analysis by default
      result.analysis.push(sectionContent);
    }
  });
  
  // If no sections, try to parse from markdown
  if (sections.length === 0 && markdown) {
    const summaryMatch = markdown.match(/##?\s*(Executive\s+)?Summary[:\s]*([\s\S]*?)(?=##|$)/i);
    if (summaryMatch) {
      result.executiveSummary = {
        title: 'Executive Summary',
        content: summaryMatch[2].trim(),
        type: 'markdown'
      };
    }
    
    const recsMatch = markdown.match(/##?\s*(Recommendations?|Next\s+Steps?)[:\s]*([\s\S]*?)(?=##|$)/i);
    if (recsMatch) {
      result.recommendations.push({
        title: 'Recommendations',
        content: recsMatch[2].trim(),
        type: 'markdown'
      });
    }
  }
  
  return result;
};

/**
 * Export to Markdown
 */
export const exportToMarkdown = (session, styleProfile) => {
  const header = generateHeader(session, null, styleProfile);
  const content = extractStructuredContent(session, styleProfile);
  
  let md = '';
  
  // Header
  md += `# ${header.title}\n\n`;
  md += `**${header.subtitle}**\n\n`;
  md += `---\n\n`;
  md += `**Date:** ${header.date}\n\n`;
  md += `**Generated by:** ${header.generatedBy}\n\n`;
  md += `---\n\n`;
  
  // Executive Summary
  if (content.executiveSummary) {
    md += `## Executive Summary\n\n`;
    md += `${content.executiveSummary.content}\n\n`;
  }
  
  // Key Findings
  if (content.keyFindings.length > 0) {
    md += `## Key Findings\n\n`;
    content.keyFindings.forEach(finding => {
      if (finding.title !== 'Key Findings') {
        md += `### ${finding.title}\n\n`;
      }
      md += `${finding.content}\n\n`;
    });
  }
  
  // Analysis
  if (content.analysis.length > 0) {
    md += `## Analysis\n\n`;
    content.analysis.forEach(section => {
      md += `### ${section.title}\n\n`;
      md += `${section.content}\n\n`;
    });
  }
  
  // Recommendations
  if (content.recommendations.length > 0) {
    md += `## Recommendations\n\n`;
    content.recommendations.forEach(rec => {
      if (rec.title !== 'Recommendations') {
        md += `### ${rec.title}\n\n`;
      }
      md += `${rec.content}\n\n`;
    });
  }
  
  // Appendices
  if (content.appendices.length > 0) {
    md += `## Appendices\n\n`;
    content.appendices.forEach(appendix => {
      md += `### ${appendix.title}\n\n`;
      md += `${appendix.content}\n\n`;
    });
  }
  
  // Raw markdown fallback
  if (!content.executiveSummary && content.rawMarkdown) {
    md += content.rawMarkdown;
  }
  
  // Footer
  md += `\n\n---\n\n`;
  md += `*Report generated by ${BRAND.name} - ${BRAND.tagline}*\n`;
  
  // Download
  const blob = new Blob([md], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${header.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.md`;
  a.click();
  URL.revokeObjectURL(url);
  
  return md;
};

/**
 * Export to PDF
 * Uses browser print functionality with styled HTML
 */
export const exportToPDF = async (session, styleProfile, organization) => {
  const header = generateHeader(session, organization, styleProfile);
  const content = extractStructuredContent(session, styleProfile);
  
  // Create styled HTML document
  const htmlContent = generatePDFHTML(header, content, styleProfile);
  
  // Open in new window for printing
  const printWindow = window.open('', '_blank');
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  
  // Wait for styles to load then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
};

/**
 * Generate styled HTML for PDF export
 */
const generatePDFHTML = (header, content, styleProfile) => {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${header.title}</title>
  <style>
    @media print {
      @page {
        margin: 1in;
        size: A4;
      }
    }
    
    * {
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
      font-size: 11pt;
      line-height: 1.6;
      color: #1a1a1a;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    
    .header {
      border-bottom: 3px solid ${header.accentColor};
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    
    .header h1 {
      font-size: 24pt;
      margin: 0 0 5px 0;
      color: #1a1a1a;
    }
    
    .header .subtitle {
      font-size: 12pt;
      color: ${header.accentColor};
      font-weight: 600;
      margin-bottom: 15px;
    }
    
    .header .meta {
      font-size: 10pt;
      color: #666;
    }
    
    .executive-summary {
      background: linear-gradient(135deg, ${header.accentColor}10, ${header.accentColor}05);
      border-left: 4px solid ${header.accentColor};
      padding: 20px;
      margin: 25px 0;
      border-radius: 0 8px 8px 0;
    }
    
    .executive-summary h2 {
      color: ${header.accentColor};
      font-size: 14pt;
      margin-top: 0;
    }
    
    h2 {
      font-size: 14pt;
      color: #1a1a1a;
      border-bottom: 1px solid #e5e5e5;
      padding-bottom: 8px;
      margin-top: 30px;
    }
    
    h3 {
      font-size: 12pt;
      color: #333;
      margin-top: 20px;
    }
    
    ul, ol {
      margin: 10px 0;
      padding-left: 25px;
    }
    
    li {
      margin: 5px 0;
    }
    
    .section {
      margin-bottom: 25px;
    }
    
    .recommendations {
      background: #f8f9fa;
      padding: 20px;
      border-radius: 8px;
      margin: 25px 0;
    }
    
    .recommendations h2 {
      color: ${header.accentColor};
      border: none;
      margin-top: 0;
    }
    
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e5e5e5;
      font-size: 9pt;
      color: #999;
      text-align: center;
    }
    
    .brand {
      font-weight: 600;
      color: ${header.accentColor};
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0;
    }
    
    th, td {
      border: 1px solid #e5e5e5;
      padding: 8px 12px;
      text-align: left;
    }
    
    th {
      background: #f8f9fa;
      font-weight: 600;
    }
    
    code {
      background: #f1f1f1;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10pt;
    }
    
    pre {
      background: #f8f9fa;
      padding: 15px;
      border-radius: 6px;
      overflow-x: auto;
      font-size: 9pt;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${header.title}</h1>
    <div class="subtitle">${header.subtitle}</div>
    <div class="meta">
      <strong>Organization:</strong> ${header.organization}<br>
      <strong>Date:</strong> ${header.date}
    </div>
  </div>
  
  ${content.executiveSummary ? `
  <div class="executive-summary">
    <h2>Executive Summary</h2>
    <div>${formatContentForHTML(content.executiveSummary.content)}</div>
  </div>
  ` : ''}
  
  ${content.keyFindings.length > 0 ? `
  <div class="section">
    <h2>Key Findings</h2>
    ${content.keyFindings.map(f => `
      ${f.title !== 'Key Findings' ? `<h3>${f.title}</h3>` : ''}
      <div>${formatContentForHTML(f.content)}</div>
    `).join('')}
  </div>
  ` : ''}
  
  ${content.analysis.length > 0 ? `
  <div class="section">
    <h2>Analysis</h2>
    ${content.analysis.map(a => `
      <h3>${a.title}</h3>
      <div>${formatContentForHTML(a.content)}</div>
    `).join('')}
  </div>
  ` : ''}
  
  ${content.recommendations.length > 0 ? `
  <div class="recommendations">
    <h2>Recommendations</h2>
    ${content.recommendations.map(r => `
      ${r.title !== 'Recommendations' ? `<h3>${r.title}</h3>` : ''}
      <div>${formatContentForHTML(r.content)}</div>
    `).join('')}
  </div>
  ` : ''}
  
  ${content.appendices.length > 0 ? `
  <div class="section">
    <h2>Appendices</h2>
    ${content.appendices.map(a => `
      <h3>${a.title}</h3>
      <div>${formatContentForHTML(a.content)}</div>
    `).join('')}
  </div>
  ` : ''}
  
  ${!content.executiveSummary && content.rawMarkdown ? `
  <div class="section">
    <div>${formatContentForHTML(content.rawMarkdown)}</div>
  </div>
  ` : ''}
  
  <div class="footer">
    Report generated by <span class="brand">${BRAND.name}</span> - ${BRAND.tagline}
  </div>
</body>
</html>
  `;
};

/**
 * Format content for HTML (basic markdown to HTML conversion)
 */
const formatContentForHTML = (content) => {
  if (!content) return '';
  
  let html = content
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    // Bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Lists
    .replace(/^[\s]*[-•]\s+(.+)$/gm, '<li>$1</li>')
    .replace(/^[\s]*\d+\.\s+(.+)$/gm, '<li>$1</li>')
    // Paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');
  
  // Wrap lists
  html = html.replace(/(<li>.*?<\/li>)+/gs, '<ul>$&</ul>');
  
  return `<p>${html}</p>`;
};

/**
 * Export to DOCX
 * Creates a downloadable DOCX file using XML structure
 */
export const exportToDOCX = async (session, styleProfile, organization) => {
  const header = generateHeader(session, organization, styleProfile);
  const content = extractStructuredContent(session, styleProfile);
  
  // For DOCX, we'll create a simple HTML file that Word can open
  // A full implementation would use a library like docx.js
  const htmlContent = generateDOCXHTML(header, content, styleProfile);
  
  const blob = new Blob([htmlContent], { 
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${header.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.doc`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Generate HTML for DOCX export (Word-compatible HTML)
 */
const generateDOCXHTML = (header, content, styleProfile) => {
  return `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head>
  <meta charset="UTF-8">
  <title>${header.title}</title>
  <style>
    body { font-family: Calibri, sans-serif; font-size: 11pt; line-height: 1.5; }
    h1 { font-size: 18pt; color: #1a1a1a; }
    h2 { font-size: 14pt; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
    h3 { font-size: 12pt; color: #444; }
    .executive-summary { background: #f5f5f5; padding: 15px; margin: 15px 0; border-left: 4px solid ${styleProfile.colorAccent}; }
    .recommendations { background: #f9f9f9; padding: 15px; margin: 15px 0; }
    ul, ol { margin-left: 20px; }
    li { margin: 5px 0; }
  </style>
</head>
<body>
  <h1>${header.title}</h1>
  <p><strong>${header.subtitle}</strong></p>
  <p><strong>Organization:</strong> ${header.organization}<br>
  <strong>Date:</strong> ${header.date}</p>
  <hr>
  
  ${content.executiveSummary ? `
  <div class="executive-summary">
    <h2>Executive Summary</h2>
    ${formatContentForHTML(content.executiveSummary.content)}
  </div>
  ` : ''}
  
  ${content.keyFindings.length > 0 ? `
  <h2>Key Findings</h2>
  ${content.keyFindings.map(f => `
    ${f.title !== 'Key Findings' ? `<h3>${f.title}</h3>` : ''}
    ${formatContentForHTML(f.content)}
  `).join('')}
  ` : ''}
  
  ${content.analysis.length > 0 ? `
  <h2>Analysis</h2>
  ${content.analysis.map(a => `
    <h3>${a.title}</h3>
    ${formatContentForHTML(a.content)}
  `).join('')}
  ` : ''}
  
  ${content.recommendations.length > 0 ? `
  <div class="recommendations">
    <h2>Recommendations</h2>
    ${content.recommendations.map(r => `
      ${r.title !== 'Recommendations' ? `<h3>${r.title}</h3>` : ''}
      ${formatContentForHTML(r.content)}
    `).join('')}
  </div>
  ` : ''}
  
  ${content.appendices.length > 0 ? `
  <h2>Appendices</h2>
  ${content.appendices.map(a => `
    <h3>${a.title}</h3>
    ${formatContentForHTML(a.content)}
  `).join('')}
  ` : ''}
  
  ${!content.executiveSummary && content.rawMarkdown ? `
  ${formatContentForHTML(content.rawMarkdown)}
  ` : ''}
  
  <hr>
  <p style="color: #999; font-size: 9pt;">Report generated by ${BRAND.name} - ${BRAND.tagline}</p>
</body>
</html>
  `;
};

export default {
  exportToMarkdown,
  exportToPDF,
  exportToDOCX
};
